import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import debounce from "lodash.debounce";

import { useUpdateTransactionNote } from "./useUpdateTransactionNote";

const NOTE_DEBOUNCE_MS = 500;
const CLEAR_SAVED_MS = 5000;

export const NOTE_MAX_LENGTH = 300;

const isWithinLimit = (text: string) =>
  Array.from(text).length <= NOTE_MAX_LENGTH;

export type NoteSaveStatus = "idle" | "saving" | "saved" | "failed";

export interface UseTransactionNoteOptions {
  isPanelOpen: boolean;
  onFailureOutOfView: () => void;
}

export interface UseTransactionNoteResult {
  value: string;
  status: NoteSaveStatus;
  onChange: (next: string) => void;
  onBlur: () => void;
}

type PendingSave = { transactionId: string; text: string };

export const useTransactionNote = (
  transaction: API.Transaction,
  { isPanelOpen, onFailureOutOfView }: UseTransactionNoteOptions
): UseTransactionNoteResult => {
  const noteOnRow = transaction.note ?? "";

  const [draft, setDraft] = useState(noteOnRow);
  const [status, setStatus] = useState<NoteSaveStatus>("idle");
  const [seededId, setSeededId] = useState<string>(transaction.id);

  const pendingRef = useRef<PendingSave | null>(null);
  // Keyed by transactionId so a new row's debounce cannot overwrite the previous
  // row's queued save.
  const queuedSavesRef = useRef(new Map<string, PendingSave>());
  const inFlightRef = useRef(false);
  const lastSavedRef = useRef<PendingSave>({
    transactionId: transaction.id,
    text: noteOnRow,
  });
  // Read when a save resolves, so it can tell whether its row is still the one on screen.
  const onScreenRef = useRef({ transactionId: transaction.id, isPanelOpen });
  onScreenRef.current = { transactionId: transaction.id, isPanelOpen };

  // Seeded here rather than in an effect: an effect paints one frame of the previous transaction's
  // note while arrowing through rows. Guarded on the id alone, so a background refetch of the same
  // row cannot overwrite what the user is still typing.
  if (seededId !== transaction.id) {
    setSeededId(transaction.id);
    setDraft(noteOnRow);
    setStatus("idle");
    lastSavedRef.current = { transactionId: transaction.id, text: noteOnRow };
  }

  const isOnScreen = (transactionId: string) =>
    onScreenRef.current.isPanelOpen &&
    onScreenRef.current.transactionId === transactionId;

  const { mutateAsync } = useUpdateTransactionNote();

  // Declared ahead of commit so the debounce, the flush, and commit's own drain all reach the
  // current one without depending on definition order.
  const commitRef = useRef<(pending: PendingSave) => void>(() => {});

  const commit = useCallback(
    async (pending: PendingSave) => {
      // Two writes in flight at once can land out of order, leaving the server holding the older
      // note. Queue instead, and let the settling save send whatever the newest text is by then, so
      // a fast typist produces one follow-up request rather than one per burst.
      if (inFlightRef.current) {
        queuedSavesRef.current.set(pending.transactionId, pending);
        return;
      }

      // The serializer trims, and success deliberately never refetches, so sending raw text would
      // leave the optimistic cache holding a value the server does not have.
      const text = pending.text.trim();
      const target = { transactionId: pending.transactionId, text };
      const lastSaved = lastSavedRef.current;

      // pending can be a queued save for a row the user has since left, in which case
      // pendingRef already holds a newer, uncommitted draft for the current row. Only clear
      // it if it still is the value being processed here.
      if (pendingRef.current === pending) {
        pendingRef.current = null;
      }
      queuedSavesRef.current.delete(target.transactionId);

      try {
        // Stops a blur from repeating a save the debounce already sent.
        if (
          lastSaved.transactionId === target.transactionId &&
          lastSaved.text === text
        ) {
          // The field agrees with the server again, so a banner from an earlier attempt is stale.
          if (isOnScreen(target.transactionId)) {
            setStatus((current) => (current === "failed" ? "idle" : current));
          }
          return;
        }

        // Reachable from flush/blur, not just onChange, so the limit still has to gate the save
        // here even though onChange already flags it the moment it happens.
        if (!isWithinLimit(text)) {
          if (isOnScreen(target.transactionId)) {
            setStatus("failed");
          }
          return;
        }

        inFlightRef.current = true;
        if (isOnScreen(target.transactionId)) {
          setStatus("saving");
        }

        await mutateAsync({ transactionId: target.transactionId, note: text });
        // Row changes reseed lastSavedRef, so a late save for a departed row must not stomp it.
        if (lastSavedRef.current.transactionId === target.transactionId) {
          lastSavedRef.current = target;
        }
        if (isOnScreen(target.transactionId)) {
          setStatus("saved");
        }
      } catch {
        if (isOnScreen(target.transactionId)) {
          setStatus("failed");
        } else {
          onFailureOutOfView();
        }
      } finally {
        // Drain on every exit; a save ending on the dedupe or over-limit return must not
        // strand the saves queued behind it.
        inFlightRef.current = false;
        const [next] = queuedSavesRef.current.values();
        if (next) {
          commitRef.current(next);
        }
      }
    },
    [mutateAsync, onFailureOutOfView]
  );

  // The debounced wrapper is created once, so it reads the current commit through the ref rather
  // than being rebuilt on every render and losing its pending timer.
  commitRef.current = commit;

  const debouncedCommit = useMemo(
    () =>
      debounce(() => {
        const pending = pendingRef.current;
        if (pending) {
          commitRef.current(pending);
        }
      }, NOTE_DEBOUNCE_MS),
    []
  );

  const flush = useCallback(() => {
    debouncedCommit.cancel();
    const pending = pendingRef.current;
    if (pending) {
      commitRef.current(pending);
    }
  }, [debouncedCommit]);

  const onChange = useCallback(
    (next: string) => {
      setDraft(next);
      // Carries the id so a later flush saves this text against the row it was typed into, even
      // once React has re-rendered with the next row.
      pendingRef.current = { transactionId: transaction.id, text: next };

      // commit() won't run for another 500ms; flag it now instead of leaving the field looking
      // fine while it's already past what a save would accept.
      if (!isWithinLimit(next.trim())) {
        setStatus("failed");
        return;
      }

      debouncedCommit();
    },
    [debouncedCommit, transaction.id]
  );

  // Flushes on the way out of a row, and on unmount. The section itself never unmounts on
  // navigation or close, so this and the blur handler are the only chances to save.
  useEffect(() => flush, [transaction.id, flush]);

  useEffect(() => {
    if (status !== "saved") {
      return undefined;
    }
    const timer = setTimeout(() => setStatus("idle"), CLEAR_SAVED_MS);
    return () => clearTimeout(timer);
  }, [status]);

  // A status left over from a save that resolved after the panel closed would still be on screen
  // when the same row is reopened.
  useEffect(() => {
    if (!isPanelOpen) {
      setStatus("idle");
    }
  }, [isPanelOpen]);

  return { value: draft, status, onChange, onBlur: flush };
};
